import { useState } from "react";
import { Link } from "react-router-dom";
import { config } from "@/config";
import { getIdToken } from "@/lib/firebase";

/** The single feedback entry point (header FEEDBACK button): a private report to the desk,
 *  with the public IDEAS board (post / vote / comment) one click away at the top. Feature-shaped
 *  entries carry a weight — "I'd pay for this" is the roadmap-pricing signal we actually act on. */

const CATS = [
  { key: "bug", label: "Bug" },
  { key: "improvement", label: "Improvement" },
  { key: "feature", label: "Feature request" },
  { key: "other", label: "Other" },
] as const;

const WEIGHTS = [
  { key: "nice", label: "Nice to have" },
  { key: "important", label: "Important" },
  { key: "would_pay", label: "I'd pay for this" },
  { key: "blocking", label: "Blocks my workflow" },
] as const;

export function FeedbackModal({ onClose }: { onClose: () => void }) {
  const [category, setCategory] = useState<string>("bug");
  const [weight, setWeight] = useState<string | null>(null);
  const [body, setBody] = useState("");
  const [state, setState] = useState<"idle" | "busy" | "sent" | "error">("idle");
  const [err, setErr] = useState<string | null>(null);
  const wantsWeight = category === "feature" || category === "improvement";

  async function submit() {
    if (!body.trim()) return;
    setState("busy");
    setErr(null);
    try {
      const token = await getIdToken();
      const r = await fetch(`${config.restUrl}/api/feedback`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "content-type": "application/json" },
        body: JSON.stringify({
          category,
          weight: wantsWeight ? weight : null,
          body: body.trim(),
          route: window.location.pathname,
        }),
      });
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      if (!r.ok) throw new Error(j.error ?? `HTTP ${r.status}`);
      setState("sent");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "failed to send");
      setState("error");
    }
  }

  return (
    <div className="fb-backdrop" onClick={onClose}>
      <div className="fb-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fb-head">
          <span className="t">FEEDBACK & IDEAS</span>
          <button className="x" onClick={onClose} title="Close">✕</button>
        </div>

        {state === "sent" ? (
          <div className="fb-body">
            <p className="fb-thanks">Logged — it goes straight to the desk. Founding-cohort requests carry real weight in what gets built next.</p>
            <Link className="fb-ideas" to="/suggestions" onClick={onClose}>
              <b>Want others to vote on it?</b> Post it on the public IDEAS board →
            </Link>
            <button className="auth-btn primary" onClick={onClose}>DONE</button>
          </div>
        ) : (
          <div className="fb-body">
            <Link className="fb-ideas" to="/suggestions" onClick={onClose}>
              <b>IDEAS BOARD</b> — propose features publicly, vote & comment →
            </Link>
            <div className="fb-quiet">Or report privately to the desk:</div>
            <div className="fb-row">
              {CATS.map((c) => (
                <button key={c.key} className={`fb-chip${category === c.key ? " on" : ""}`} onClick={() => setCategory(c.key)}>
                  {c.label}
                </button>
              ))}
            </div>

            <textarea
              className="fb-text"
              maxLength={2000}
              placeholder={category === "bug" ? "What broke, and what did you expect to happen?" : "What do you want, and what would it let you do?"}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />

            {wantsWeight && (
              <div className="fb-weight">
                <span className="lbl">How much does it matter?</span>
                <div className="fb-row">
                  {WEIGHTS.map((w) => (
                    <button key={w.key} className={`fb-chip${weight === w.key ? " on" : ""}`} onClick={() => setWeight(weight === w.key ? null : w.key)}>
                      {w.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {err && <div className="auth-error">{err}</div>}
            <button className="auth-btn primary" disabled={state === "busy" || !body.trim()} onClick={() => void submit()}>
              {state === "busy" ? "SENDING…" : "SEND TO THE DESK"}
            </button>
            <p className="fb-alt">Private to the desk. Want other operators to vote on it? Post it on the <a href="/suggestions">suggestions board</a> instead.</p>
          </div>
        )}
      </div>
    </div>
  );
}
